import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import AuthGuard from '@/components/auth-guard';
import {
  EntityVisual,
  SectionDisplay
} from '@/components/entity-display';
import { FeatureGuard } from '@/components/feature-guard';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { useCodexAccessLayer } from '@/features/codex';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export const Route = createFileRoute('/codex/view/$id')({
  component: CodexViewPage,
});

function ViewCodexPage() {
  const navigate = useNavigate();
  const { id: entryId } = Route.useParams();
  const { novel } = useNovelRepository();
  const { t } = useTranslation();

  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    entry,
    sections: customFieldSections,
    isLoading,
    deleteEntry,
  } = useCodexAccessLayer(entryId);

  const handleDelete = async () => {
    if (!entryId) return;
    setIsDeleting(true);
    try {
      await deleteEntry(entryId);
      toast({
        title: t('entity_form.codex.delete_success_title'),
        description: t('entity_form.codex.delete_success_desc'),
      });
      navigate({ to: '/codex' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  // Loading or waiting state
  if (isLoading || (!entry && !novel.id)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not Found
  if (!entry) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="text.secondary">{t('entity_view.codex.not_found_title')}</Typography>
        <Typography variant="body1">{t('entity_view.codex.not_found_desc')}</Typography>
        <Button onClick={() => navigate({ to: '/codex' })} sx={{ mt: 2 }}>{t('entity_view.codex.back')}</Button>
      </Box>
    );
  }

  return (
    <Page
      title={entry.name || t('entity_view.codex.view_fallback')}
      description={entry.type ? t(`entity_list.codex.categories.${entry.type}`, { defaultValue: novel?.codexCategories?.[entry.type] || entry.type }) : undefined}
      breadcrumbs={[
        { href: '/codex', label: t('entity_list.codex.title') },
        { label: entry.name || t('entity_view.codex.view_fallback') }
      ]}
      layoutMode="scrolling"
      actions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setShowDeleteConfirm(true)}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_form.common.delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate({ to: '/codex/edit/$id', params: { id: entryId } })}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_view.codex.edit')}
          </Button>
        </Stack>
      }
    >

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={4}>
            {entry.content && (
              <Paper sx={{ p: 4, borderRadius: 2 }}>
                <Box
                  className="prose prose-sm dark:prose-invert"
                  sx={{
                    '& h1': { fontSize: '1.8rem', fontWeight: 600, mb: 2 },
                    '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 3, mb: 1.5 },
                    '& p': { mb: 2, lineHeight: 1.7, fontSize: '1rem' },
                    '& ul, & ol': { mb: 2, pl: 3 },
                    '& li': { mb: 1 },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.content}</ReactMarkdown>
                </Box>
              </Paper>
            )}

            {/* Structured Data Section */}
            {customFieldSections.map((section) => (
              <SectionDisplay
                key={section.id}
                title={section.title}
                fields={section.fields}
                data={entry}
              />
            ))}
          </Stack>
        </Grid>

        <EntityVisual
          title={t('entity_view.codex.visual')}
          images={entry.images}
          primaryImageId={entry.primaryImageId}
          altText={entry.name || 'Codex entry'}
        />
      </Grid>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemName={entry?.name}
        description={t('entity_form.codex.delete_desc')}
      />
    </Page>
  );
}

function CodexViewPage() {
  return (
    <FeatureGuard featureKey="should_display_codex_page">
      <AuthGuard>
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
          <ViewCodexPage />
        </Suspense>
      </AuthGuard>
    </FeatureGuard>
  );
}
